import React, { useEffect, useMemo, useState } from 'react';
import { CheckOutlined, DollarCircleOutlined, SearchOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Empty, Input, Space, Tooltip, Typography } from 'antd';

import { DEFAULT_CURRENCY } from '@/constants/currency';
import { useSelectedCurrency } from '@/contexts/SelectedCurrencyContext';
import { useCurrencies } from '@/services/ant-design-pro/queries';
import HeaderDropdown from '../HeaderDropdown';

const CurrencySelector: React.FC = () => {
  const { selectedCurrency, setSelectedCurrency } = useSelectedCurrency();
  const { data: currencies = [], isLoading } = useCurrencies();
  const displayedCurrency = selectedCurrency.trim() || DEFAULT_CURRENCY;
  const [search, setSearch] = useState('');
  const filteredCurrencies = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return currencies;
    }

    return currencies.filter((currency) => currency.code.toLowerCase().includes(normalizedSearch));
  }, [search, currencies]);
  useEffect(() => {
    if (!currencies.length) {
      return;
    }

    const hasSelectedCurrency = currencies.some((currency) => currency.code === selectedCurrency);
    if (hasSelectedCurrency) {
      return;
    }

    const fallbackCurrency = currencies.find((currency) => currency.code === DEFAULT_CURRENCY);
    const nextCurrency = fallbackCurrency ?? currencies[0];
    setSelectedCurrency(nextCurrency.code, nextCurrency.type);
  }, [currencies, selectedCurrency, setSelectedCurrency]);

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    const nextCurrency = currencies.find((currency) => currency.code === String(key));
    setSelectedCurrency(String(key), nextCurrency?.type);
  };

  const menuItems = filteredCurrencies.map((currency) => ({
    key: currency.code,
    style: {
      paddingInline: 10,
      borderRadius: 8,
      marginInline: 6,
      marginBlock: 2,
    },
    label: (
      <div
        style={{
          minWidth: 180,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <Space size={8}>
          <Typography.Text strong>{currency.code}</Typography.Text>
          <Typography.Text type="secondary" style={{ textTransform: 'capitalize', fontSize: 12 }}>
            {currency.type}
          </Typography.Text>
        </Space>
        {currency.code === selectedCurrency ? (
          <CheckOutlined style={{ fontSize: 12, color: '#1677ff' }} />
        ) : null}
      </div>
    ),
  }));

  return (
    <HeaderDropdown
      trigger={['click']}
      menu={{
        selectedKeys: [displayedCurrency],
        selectable: true,
        onClick: onMenuClick,
        style: {
          boxShadow: 'none',
          borderInlineEnd: 'none',
          paddingBlock: 4,
        },
        items: menuItems,
      }}
      placement="bottomRight"
      dropdownRender={(menu) => (
        <div
          style={{
            width: 240,
            borderRadius: 12,
            border: '1px solid rgba(5, 5, 5, 0.08)',
            background: '#fff',
            boxShadow: '0 6px 16px -8px rgba(0, 0, 0, 0.08), 0 9px 28px 0 rgba(0, 0, 0, 0.05)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '10px 10px 8px',
              borderBottom: '1px solid rgba(5, 5, 5, 0.06)',
              background:
                'linear-gradient(180deg, rgba(22, 119, 255, 0.06) 0%, rgba(22, 119, 255, 0.02) 100%)',
            }}
          >
            <Typography.Text
              strong
              style={{ display: 'block', marginBottom: 8, fontSize: 12, letterSpacing: 0.3 }}
            >
              Select Currency
            </Typography.Text>
            <Input
              size="small"
              placeholder="Search currency"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              prefix={<SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.35)' }} />}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto', paddingBlock: 6 }}>
            {filteredCurrencies.length ? (
              menu
            ) : (
              <div style={{ padding: '20px 12px' }}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No currencies" />
              </div>
            )}
          </div>
        </div>
      )}
    >
      <Tooltip title={`Currency: ${displayedCurrency}`}>
        <Button
          type="text"
          icon={<DollarCircleOutlined />}
          loading={isLoading}
          disabled={!currencies.length && !isLoading}
          aria-label={`Selected currency ${displayedCurrency}`}
          style={{
            padding: '4px 10px',
            borderRadius: 999,
            border: '1px solid rgba(5, 5, 5, 0.08)',
            background: 'rgba(255, 255, 255, 0.72)',
            backdropFilter: 'blur(8px)',
          }}
        >
          {displayedCurrency}
        </Button>
      </Tooltip>
    </HeaderDropdown>
  );
};

export default CurrencySelector;
